import { Text, TextProps } from '@mantine/core';
import { convertLegacyEmojis, EMOJI_REGEX } from '@vision/ui/context';
import { useEffect, useState } from 'react';

interface TextWithEmojiProps extends Omit<TextProps, 'children'> {
  text: string;
}

// Global initialization state for emoji-mart
let emojiMartInitialized = false;
let emojiMartInitPromise: Promise<void> | null = null;

async function ensureEmojiMartInitialized(): Promise<void> {
  if (emojiMartInitialized) {
    return;
  }

  if (emojiMartInitPromise) {
    return emojiMartInitPromise;
  }

  emojiMartInitPromise = (async () => {
    const [{ default: emojiData }, { init }] = await Promise.all([import('@emoji-mart/data'), import('emoji-mart')]);
    await init({ data: emojiData });
    emojiMartInitialized = true;
  })();

  return emojiMartInitPromise;
}

export function TextWithEmoji({ text, size, ...rest }: TextWithEmojiProps) {
  const [isReady, setIsReady] = useState(emojiMartInitialized);

  useEffect(() => {
    if (!emojiMartInitialized) {
      ensureEmojiMartInitialized().then(() => setIsReady(true));
    }
  }, []);

  const parts = convertLegacyEmojis(text);

  // Render text without emoji elements until emoji-mart is initialized
  if (!isReady) {
    return <Text {...rest}>{text}</Text>;
  }

  return (
    <Text {...rest}>
      {parts.split(EMOJI_REGEX).map((part, index) => {
        if (EMOJI_REGEX.test(part)) {
          return <em-emoji key={index} shortcodes={part} size={size} />;
        }
        return part;
      })}
    </Text>
  );
}
